import type { z } from 'zod'
import type { apiZod } from '@/shared/api'

// Разворачиваем snake_case DTO бэка в camelCase-форму; enum-поля валидирует
// доменная Zod-схема на выходе (см. queries.ts).

type MeetingSummaryDto = z.infer<typeof apiZod.zMeetingSummaryView>
type MeetingDetailDto = z.infer<typeof apiZod.zMeetingDetailView>
type DetailParticipantDto = z.infer<typeof apiZod.zDetailParticipantView>
type MeetingViewDto = z.infer<typeof apiZod.zMeetingView>
type LobbyParticipantDto = z.infer<typeof apiZod.zLobbyParticipantView>

export function toMeetingSummary(dto: MeetingSummaryDto) {
  return {
    id: dto.id,
    title: dto.title,
    state: dto.state,
    role: dto.role,
    tags: dto.tags ?? [],
    companyId: dto.company_id || undefined,
    projectId: dto.project_id || undefined,
    companyName: dto.company_name || undefined,
    projectName: dto.project_name || undefined,
    // participant_count в OpenAPI — int64 → z.coerce.bigint(); доменная схема ждёт number.
    participantCount: Number(dto.participant_count),
    // Состав приезжает с выкатом бэка; его отсутствие читаем как пустой —
    // карточка просто не покажет строку имён.
    roster: {
      kind: dto.roster?.kind ?? 'invited',
      total: Number(dto.roster?.total ?? 0),
      names: dto.roster?.names ?? [],
    },
    locked: dto.locked ?? false,
    private: dto.private ?? false,
    // Поле приезжает с выкатом бэка; его отсутствие читаем как «не открыта» —
    // безопасный дефолт (`00-architecture § 5`).
    customerVisible: dto.customer_visible ?? false,
    scheduledAt: dto.scheduled_at,
    startedAt: dto.started_at,
    endedAt: dto.ended_at,
    createdAt: dto.created_at,
    matchSources: dto.match_sources ?? [],
  }
}

export function toDetailParticipant(dto: DetailParticipantDto) {
  return {
    participantId: dto.participant_id,
    kind: dto.kind,
    name: dto.name,
    username: dto.username,
    state: dto.state,
    departments: dto.departments ?? [],
  }
}

export function toMeetingDetail(dto: MeetingDetailDto) {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description,
    tags: dto.tags ?? [],
    companyId: dto.company_id || undefined,
    projectId: dto.project_id || undefined,
    companyName: dto.company_name || undefined,
    projectName: dto.project_name || undefined,
    state: dto.state,
    isOrganizer: dto.is_organizer ?? false,
    locked: dto.locked ?? false,
    private: dto.private ?? false,
    customerVisible: dto.customer_visible ?? false,
    customerVisibleBy: dto.customer_visible_by || undefined,
    customerVisibleByName: dto.customer_visible_by_name || undefined,
    customerVisibleAt: dto.customer_visible_at || undefined,
    organizer: { name: dto.organizer.name, subject: dto.organizer.subject },
    participants: (dto.participants ?? []).map(toDetailParticipant),
    invitees: (dto.invitees ?? []).map((i) => ({
      subject: i.subject,
      ...(i.name ? { name: i.name } : {}),
      ...(i.username ? { username: i.username } : {}),
      ...(i.invited_by ? { invitedBy: i.invited_by } : {}),
      joined: i.joined,
    })),
    recording: { exists: dto.recording.exists, state: dto.recording.state },
    transcriptStatus: dto.transcript_status,
    // transcript_progress в OpenAPI — int64 → bigint; доменная схема ждёт number.
    transcriptProgress: Number(dto.transcript_progress ?? 0),
    guestLink: dto.guest_link,
    canDelete: dto.can_delete,
    canManageAccess: dto.can_manage_access ?? false,
    scheduledAt: dto.scheduled_at,
    startedAt: dto.started_at,
    endedAt: dto.ended_at,
    createdAt: dto.created_at,
  }
}

export function toLobbyParticipant(dto: LobbyParticipantDto) {
  return {
    participantId: dto.participant_id,
    kind: dto.kind,
    name: dto.name,
    state: dto.state,
  }
}

type TagSuggestionDto = z.infer<typeof apiZod.zTagDictSuggestion>

export function toTagSuggestion(dto: TagSuggestionDto) {
  return {
    name: dto.name,
    // usage_count в OpenAPI — int64 → bigint; доменная схема ждёт number.
    usageCount: Number(dto.usage_count),
  }
}

export function toCreatedMeeting(dto: MeetingViewDto) {
  return {
    id: dto.id,
    title: dto.title,
    state: dto.state,
    guestLink: dto.guest_link,
    companyId: dto.company_id || undefined,
    projectId: dto.project_id || undefined,
    // Пометку подтверждает бэк: форма просила, а решил инвариант агрегата —
    // в ответе то, что получилось на самом деле. Отсутствие поля (окно выката
    // старого бэка) читаем как «не открыта» — безопасный дефолт.
    customerVisible: dto.customer_visible ?? false,
    scheduledAt: dto.scheduled_at,
  }
}
